import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { getToken, getUser } from '@/lib/api';
import { toast } from 'sonner';

interface AuthGuardOptions {
  requireAuth?: boolean;
  allowedRoles?: string[];
  redirectTo?: string;
}

/**
 * 认证守卫Hook
 * 用于保护页面，确保用户已登录且有正确的角色权限
 */
export function useAuthGuard(options: AuthGuardOptions = {}) {
  const {
    requireAuth = true,
    allowedRoles = [],
    redirectTo = '/login',
  } = options;

  const router = useRouter();
  const pathname = usePathname();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<{ username: string; role: string } | null>(null);

  useEffect(() => {
    // 如果不需要认证，直接返回
    if (!requireAuth) {
      // 使用 setTimeout 避免同步 setState
      setTimeout(() => {
        setIsChecking(false);
        setIsAuthenticated(true);
        setUser(getUser());
      }, 0);
      return;
    }

    // 仅在客户端执行
    if (typeof window === 'undefined') {
      return;
    }

    const token = getToken();
    const currentUser = getUser();

    // 检查是否已登录
    if (!token || !currentUser) {
      console.warn('[AuthGuard] 未登录，重定向到登录页');
      setTimeout(() => {
        setIsChecking(false);
        setIsAuthenticated(false);
        toast.error('请先登录');
        router.push(`${redirectTo}?redirect=${encodeURIComponent(pathname)}`);
      }, 0);
      return;
    }

    // 检查角色权限（teacher等同于admin）
    if (allowedRoles.length > 0) {
      // teacher等同于admin，自动通过admin和teacher的权限检查
      const userRole = currentUser.role;
      const effectiveRole = userRole === 'teacher' ? ['teacher', 'admin'] : [userRole];
      const hasPermission = allowedRoles.some(role => effectiveRole.includes(role));
      
      if (!hasPermission) {
        console.warn(`[AuthGuard] 角色不匹配: ${currentUser.role}, 需要: ${allowedRoles.join(', ')}`);
        setTimeout(() => {
          setIsChecking(false);
          toast.error('无权访问此页面');
          
          // 根据用户角色重定向到对应页面
          if (currentUser.role === 'teacher' || currentUser.role === 'admin') {
            router.push('/teacher');
          } else if (currentUser.role === 'student') {
            router.push('/student');
          } else {
            router.push('/login');
          }
        }, 0);
        return;
      }
    }

    // 使用 setTimeout 避免同步 setState
    setTimeout(() => {
      setIsAuthenticated(true);
      setUser(currentUser);
      setIsChecking(false);
    }, 0);
  }, [requireAuth, allowedRoles, redirectTo, router, pathname]);

  return {
    isAuthenticated,
    isChecking,
    user,
  };
}

/**
 * 学生页面守卫Hook
 */
export function useStudentGuard() {
  return useAuthGuard({
    requireAuth: true,
    allowedRoles: ['student', 'admin'],
    redirectTo: '/login',
  });
}

/**
 * 教师页面守卫Hook
 */
export function useTeacherGuard() {
  return useAuthGuard({
    requireAuth: true,
    allowedRoles: ['teacher', 'admin'],
    redirectTo: '/login',
  });
}
